import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { ChevronRight, Lock, ExternalLink, Truck, Plane, Ship, Zap } from 'lucide-react';
import { Container } from '../components/layout/Primitives';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { useCart } from '../hooks/useCart';
import { formatPrice } from '../utils/formatPrice';
import { isApiEnabled } from '../config/env';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { registerThunk, loginThunk } from '../store/actions/authActions';
import { ensureCartSessionId, selectCartSessionId } from '../store/actions/cartActions';
import { createOrder, clearOrderError } from '../store/actions/orderActions';
import { initializePayment, verifyPayment, clearPaymentError } from '../store/actions/paymentActions';
import { fetchActivePaymentMethods, clearPaymentMethodError } from '../store/actions/paymentMethodActions';
import { validateCoupon, clearCouponError } from '../store/actions/couponActions';
import { DEFAULT_SETTINGS } from '../store/reducers/storeSettingsReducer';
import { paymentService } from '../services/paymentService';
import PageSeo from '../components/seo/PageSeo';
import { productService } from '../services/productService';
import { SUPPORTED_LOCALES } from '../config/locales';

const MIN_PW = 6;

function normalizeCountryCode(input) {
  const raw = String(input || '').trim();
  if (!raw) return '';
  const upper = raw.toUpperCase();
  const map = {
    USA: 'US',
    'UNITED STATES': 'US',
    US: 'US',
    UK: 'GB',
    'UNITED KINGDOM': 'GB',
    GB: 'GB',
    ENGLAND: 'GB',
    FRANCE: 'FR',
    FR: 'FR',
    GERMANY: 'DE',
    DE: 'DE',
    SPAIN: 'ES',
    ES: 'ES',
    ITALY: 'IT',
    IT: 'IT',
    PORTUGAL: 'PT',
    PT: 'PT',
    NETHERLANDS: 'NL',
    NL: 'NL',
    AUSTRALIA: 'AU',
    AU: 'AU',
    CANADA: 'CA',
    CA: 'CA',
    MEXICO: 'MX',
    MX: 'MX',
    ARGENTINA: 'AR',
    AR: 'AR',
    COLOMBIA: 'CO',
    CO: 'CO',
  };
  if (map[upper]) return map[upper];
  if (/^[A-Z]{2}$/.test(upper)) return upper;
  return upper;
}

function Field({ label, value, onChange, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="font-mono-luxe" style={{ fontSize: '11px', opacity: 0.55 }}>
        {label}
      </span>
      <Input
        {...rest}
        value={value}
        onChange={onChange}
        style={{ marginTop: '0.5rem' }}
      />
    </label>
  );
}

function SelectField({ label, value, onChange, children, ...rest }) {
  return (
    <label style={{ display: 'block' }}>
      <span className="font-mono-luxe" style={{ fontSize: '11px', opacity: 0.55 }}>
        {label}
      </span>
      <select
        {...rest}
        value={value}
        onChange={onChange}
        style={{
          marginTop: '0.5rem',
          width: '100%',
          height: '42px',
          border: '1px solid hsl(35,18%,84%)',
          background: '#fff',
          padding: '0 0.75rem',
          fontSize: '0.9rem',
        }}
      >
        {children}
      </select>
    </label>
  );
}

const SHIPPING_LIST_SCROLL_STYLE = {
  maxHeight: '250px',
  overflowY: 'auto',
  border: '1px solid hsl(35,18%,84%)',
  padding: '0 0.75rem',
  background: '#fff',
};

function unwrapCreatedOrder(raw) {
  const o = raw?.data ?? raw;
  return o?._id ? o : raw?.data?.data ?? o;
}

function sortByQuickest(options) {
  const rows = Array.isArray(options) ? [...options] : [];
  return rows.sort((a, b) => {
    const aEta = Number.isFinite(a?.etaMinDays) ? Number(a.etaMinDays) : Number.MAX_SAFE_INTEGER;
    const bEta = Number.isFinite(b?.etaMinDays) ? Number(b.etaMinDays) : Number.MAX_SAFE_INTEGER;
    if (aEta !== bEta) return aEta - bEta;
    const aMax = Number.isFinite(a?.etaMaxDays) ? Number(a.etaMaxDays) : Number.MAX_SAFE_INTEGER;
    const bMax = Number.isFinite(b?.etaMaxDays) ? Number(b.etaMaxDays) : Number.MAX_SAFE_INTEGER;
    if (aMax !== bMax) return aMax - bMax;
    return String(a?.methodName || '').localeCompare(String(b?.methodName || ''));
  });
}

function shippingMethodIcon(methodName = '') {
  const label = String(methodName || '').toLowerCase();
  if (/(express|priority|fast|dhl|fedex|ups|air)/.test(label)) return Plane;
  if (/(sea|ocean|cargo|vessel)/.test(label)) return Ship;
  if (/(premium|same day|next day|urgent)/.test(label)) return Zap;
  return Truck;
}

export default function Checkout() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { items, subtotal, clear } = useCart();
  const cartSessionId = useAppSelector(selectCartSessionId);
  const authToken = useAppSelector((s) => s.auth?.token);
  const user = useAppSelector((s) => s.auth?.user);
  const settings = useAppSelector((s) => s.storeSettings?.settings);
  const activeMethods = useAppSelector((s) => s.paymentMethod?.activeMethods ?? []);
  const paymentMethodsLoading = useAppSelector((s) => s.paymentMethod?.loading ?? false);
  const couponValidating = useAppSelector((s) => s.coupon?.validating ?? false);
  const shipCfg = settings?.shippingPricing || DEFAULT_SETTINGS.shippingPricing;
  const freeThreshold = Number(shipCfg.freeShippingThreshold) || 500;
  const flatShip = Number(shipCfg.flatShippingRate) || 15;
  const taxPct = Number(shipCfg.taxRatePercent) || 8;
  const useFixedCheckoutShipping = shipCfg?.useFixedCheckoutShipping !== false;
  const fixedCheckoutShipping =
    shipCfg?.fixedCheckoutShipping != null
      ? Math.max(0, Number(shipCfg.fixedCheckoutShipping) || 0)
      : 10;

  const [flowStep, setFlowStep] = useState(() => (authToken ? 'shipping' : 'account'));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [shippingQuote, setShippingQuote] = useState({ mode: 'none', options: [], packages: [] });
  const [shippingLoading, setShippingLoading] = useState(false);
  const [selectedShippingKey, setSelectedShippingKey] = useState('');
  const [selectedPackageKeys, setSelectedPackageKeys] = useState({});

  const [account, setAccount] = useState({
    email: '',
    password: '',
    confirm: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  const [shipping, setShipping] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    apartment: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'US',
  });

  const [selectedMethod, setSelectedMethod] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [bankDetails, setBankDetails] = useState(null);
  const [paymentLink, setPaymentLink] = useState(null);
  const [emailPayInfo, setEmailPayInfo] = useState(null);

  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponInlineError, setCouponInlineError] = useState('');
  const shippingCountries = useMemo(() => {
    const map = new Map();
    for (const l of SUPPORTED_LOCALES) {
      if (l.shippingCountryCode && l.country && !map.has(l.shippingCountryCode)) {
        map.set(l.shippingCountryCode, l.country);
      }
    }
    return Array.from(map.entries())
      .map(([code, name]) => ({ code, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, []);
  useEffect(() => {
    dispatch(ensureCartSessionId());
  }, [dispatch]);

  useEffect(() => {
    if (authToken && flowStep === 'account') setFlowStep('shipping');
  }, [authToken, flowStep]);

  useEffect(() => {
    if (user) {
      setShipping((s) => ({
        ...s,
        firstName: user.firstName || s.firstName,
        lastName: user.lastName || s.lastName,
        email: user.email || s.email,
      }));
    }
  }, [user]);

  useEffect(() => {
    let cancelled = false;
    async function detectCountryFromIp() {
      try {
        const r = await fetch('https://ipapi.co/json/');
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        const cc = normalizeCountryCode(data?.country_code || data?.country || '');
        if (!cc) return;
        const allowed = shippingCountries.some((c) => c.code === cc);
        if (!allowed) return;
        setShipping((s) => {
          if (s.country && s.country !== 'US') return s;
          return { ...s, country: cc };
        });
      } catch {
        // keep default
      }
    }
    detectCountryFromIp();
    return () => {
      cancelled = true;
    };
  }, [shippingCountries]);

  const fallbackShippingCost = useMemo(() => {
    if (useFixedCheckoutShipping) return fixedCheckoutShipping;
    return subtotal >= freeThreshold ? 0 : flatShip;
  }, [useFixedCheckoutShipping, fixedCheckoutShipping, subtotal, freeThreshold, flatShip]);
  const singleOptions = useMemo(
    () =>
      sortByQuickest(Array.isArray(shippingQuote?.options) ? shippingQuote.options : [])
        .slice(0, 5)
        .map((o, idx) => ({
          ...o,
          key: `${o.methodName || 'shipping'}-${idx}`,
        })),
    [shippingQuote],
  );
  const singleDisplayOptions = useMemo(() => {
    const list = Array.isArray(singleOptions) ? [...singleOptions] : [];
    const fastestName = String(shippingQuote?.fastestOption?.methodName || '').trim().toLowerCase();
    if (!fastestName) return list;
    const idx = list.findIndex((o) => String(o?.methodName || '').trim().toLowerCase() === fastestName);
    if (idx <= 0) return list;
    const [fast] = list.splice(idx, 1);
    return [fast, ...list];
  }, [singleOptions, shippingQuote?.fastestOption?.methodName]);
  const selectedShippingOption = useMemo(
    () => singleOptions.find((o) => String(o.key) === String(selectedShippingKey)) || null,
    [singleOptions, selectedShippingKey],
  );
  const splitPackages = useMemo(
    () =>
      (Array.isArray(shippingQuote?.packages) ? shippingQuote.packages : []).map((pkg) => ({
        ...pkg,
        options: sortByQuickest(Array.isArray(pkg.options) ? pkg.options : [])
          .slice(0, 5)
          .map((o, idx) => ({
            ...o,
            key: `${pkg.packageId || 'pkg'}-${o.methodName || 'shipping'}-${idx}`,
          })),
      })),
    [shippingQuote],
  );
  const splitShippingCost = useMemo(
    () =>
      splitPackages.reduce((sum, pkg) => {
        const selected = pkg.options.find((o) => selectedPackageKeys[pkg.packageId] === o.key) || null;
        return sum + Number(selected?.customerShippingCost || 0);
      }, 0),
    [splitPackages, selectedPackageKeys],
  );
  const shippingCost = useFixedCheckoutShipping
    ? fixedCheckoutShipping
    :
    shippingQuote?.mode === 'single_package'
      ? selectedShippingOption
        ? Number(selectedShippingOption.customerShippingCost || 0)
        : fallbackShippingCost
      : shippingQuote?.mode === 'split_packages'
        ? splitShippingCost
        : fallbackShippingCost;

  const discountAmount = appliedCoupon?.discountAmount ?? 0;
  const discountedSubtotal = Math.max(0, subtotal - discountAmount);
  const tax = useMemo(
    () => Math.round((discountedSubtotal * taxPct) / 100),
    [discountedSubtotal, taxPct],
  );
  const total = discountedSubtotal + shippingCost + tax;

  const cartFingerprint = useMemo(
    () => items.map((it) => `${it.product?.mongoId || it.product?.id}:${it.qty}:${it.product?.price}`).join('|'),
    [items],
  );

  useEffect(() => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponInlineError('');
    dispatch(clearCouponError());
  }, [cartFingerprint, dispatch]);

  const breadcrumbLabel = useMemo(() => {
    if (!authToken) {
      if (flowStep === 'account') return t('modamai.checkout.crumbAccountGuest1');
      if (flowStep === 'shipping') return t('modamai.checkout.crumbShippingGuest2');
      if (flowStep === 'payment') return t('modamai.checkout.crumbPaymentGuest3');
    } else {
      if (flowStep === 'shipping') return t('modamai.checkout.crumbShippingUser1');
      if (flowStep === 'payment') return t('modamai.checkout.crumbPaymentUser2');
    }
    return '';
  }, [authToken, flowStep, t]);

  useEffect(() => {
    if (flowStep !== 'payment' || !isApiEnabled()) return undefined;
    dispatch(clearPaymentMethodError());
    dispatch(fetchActivePaymentMethods());
    return undefined;
  }, [flowStep, dispatch]);

  useEffect(() => {
    if (flowStep !== 'payment') return;
    const list = activeMethods;
    const def = list.find((m) => m.isDefault) || list[0];
    setSelectedMethod(def || null);
  }, [flowStep, activeMethods]);

  const runVerifyPayment = useCallback(
    async (txRef, gateway) => {
      setError('');
      dispatch(clearPaymentError());
      const r = await dispatch(verifyPayment(txRef, gateway));
      if (r.success) {
        const inner = r.data?.data ?? r.data;
        const orderNum = inner?.orderNumber ?? r.data?.orderNumber;
        if (orderNum || inner?.orderId) {
          clear();
          setOrderNumber(orderNum || '');
          setFlowStep('confirmation');
          setSearchParams({}, { replace: true });
        } else {
          setError(t('modamai.checkout.errorPaymentIncomplete'));
        }
      } else {
        setError(r.error || t('modamai.checkout.errorPaymentVerify'));
      }
    },
    [clear, dispatch, setSearchParams, t],
  );

  useEffect(() => {
    if (!isApiEnabled()) return undefined;
    const txRef = searchParams.get('tx_ref') || searchParams.get('transaction_ref');
    const status = searchParams.get('status');
    if (txRef && (status === 'successful' || status === 'success')) {
      const gateway = sessionStorage.getItem('paymentGateway') || 'flutterwave';
      sessionStorage.removeItem('paymentGateway');
      runVerifyPayment(txRef, gateway);
    }
    return undefined;
  }, [searchParams, runVerifyPayment]);

  const setShip = (key) => (e) => setShipping((s) => ({ ...s, [key]: e.target.value }));

  useEffect(() => {
    const destination = normalizeCountryCode(shipping.country);
    if (!destination || !Array.isArray(items) || items.length === 0) {
      setShippingQuote({ mode: 'none', options: [], packages: [] });
      setSelectedShippingKey('');
      setSelectedPackageKeys({});
      return;
    }
    const payloadItems = items
      .map((it) => ({
        productId: it.product?.mongoId || it.product?.id,
        variantId:
          it.product?.variantId ||
          it.product?.defaultVariantId ||
          (Array.isArray(it.product?.variants) && it.product.variants.length > 0
            ? it.product.variants[0]?.variantId || ''
            : ''),
        quantity: Number(it.qty) || 1,
      }))
      .filter((x) => x.productId);
    if (!payloadItems.length || payloadItems.every((x) => !x.variantId)) {
      setShippingQuote({ mode: 'none', options: [], packages: [] });
      setSelectedShippingKey('');
      setSelectedPackageKeys({});
      return;
    }
    let cancelled = false;
    setShippingLoading(true);
    productService
      .getCartShippingOptions({
        countryCode: destination,
        items: payloadItems,
      })
      .then((data) => {
        if (cancelled) return;
        setShippingQuote(data || { mode: 'none', options: [], packages: [] });
        if (data?.mode === 'single_package') {
          const rows = Array.isArray(data?.options) ? data.options : [];
          setSelectedShippingKey((prev) => prev || (rows[0] ? `${rows[0].methodName || 'shipping'}-0` : ''));
          setSelectedPackageKeys({});
        } else if (data?.mode === 'split_packages') {
          const next = {};
          (Array.isArray(data?.packages) ? data.packages : []).forEach((pkg) => {
            const firstOpt = Array.isArray(pkg?.options) ? pkg.options[0] : null;
            if (firstOpt) next[pkg.packageId] = `${pkg.packageId}-${firstOpt.methodName || 'shipping'}-0`;
          });
          setSelectedPackageKeys(next);
          setSelectedShippingKey('');
        } else {
          setSelectedShippingKey('');
          setSelectedPackageKeys({});
        }
      })
      .catch(() => {
        if (cancelled) return;
        setShippingQuote({ mode: 'none', options: [], packages: [] });
        setSelectedShippingKey('');
        setSelectedPackageKeys({});
      })
      .finally(() => {
        if (!cancelled) setShippingLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [items, shipping.country]);

  const handleAccountRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (account.password !== account.confirm) {
      setError(t('modamai.checkout.errorPasswordMismatch'));
      return;
    }
    if (account.password.length < MIN_PW) {
      setError(t('modamai.checkout.errorPasswordMin', { min: MIN_PW }));
      return;
    }
    setSubmitting(true);
    const res = await dispatch(
      registerThunk({
        email: account.email.trim(),
        password: account.password,
        firstName: account.firstName.trim(),
        lastName: account.lastName.trim(),
        phone: account.phone.trim() || undefined,
      }),
    );
    setSubmitting(false);
    if (res?.success) {
      setShipping((s) => ({
        ...s,
        firstName: account.firstName.trim(),
        lastName: account.lastName.trim(),
        email: account.email.trim(),
        phone: account.phone.trim() || s.phone,
      }));
      setFlowStep('shipping');
    } else {
      setError(res?.error || t('modamai.checkout.errorCouldNotCreateAccount'));
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    const res = await dispatch(
      loginThunk({ email: loginEmail.trim(), password: loginPassword }),
    );
    setSubmitting(false);
    if (res?.success) {
      setFlowStep('shipping');
    } else {
      setError(res?.error || t('modamai.checkout.errorLoginFailed'));
    }
  };

  const validateShipping = () => {
    const req = ['firstName', 'lastName', 'email', 'address', 'city', 'state', 'zipCode', 'country'];
    for (const k of req) {
      if (!String(shipping[k] || '').trim()) return false;
    }
    return true;
  };

  const handleApplyCoupon = async () => {
    setCouponInlineError('');
    dispatch(clearCouponError());
    const raw = couponInput.trim();
    if (!raw) {
      setCouponInlineError(t('modamai.checkout.errorEnterCoupon'));
      return;
    }
    const res = await dispatch(validateCoupon({ code: raw, subtotal, items }));
    if (res?.success && res.data) {
      setAppliedCoupon({
        code: res.data.code,
        discountAmount: Number(res.data.discountAmount),
      });
      setCouponInlineError('');
    } else {
      setAppliedCoupon(null);
      setCouponInlineError(res?.message || res?.error || t('modamai.checkout.invalidCoupon'));
    }
  };

  const handleRemoveCoupon = () => {
    setAppliedCoupon(null);
    setCouponInput('');
    setCouponInlineError('');
    dispatch(clearCouponError());
  };

  const advanceFromShipping = (e) => {
    e.preventDefault();
    setError('');
    if (!validateShipping()) {
      setError(t('modamai.checkout.errorCompleteAddress'));
      return;
    }
    if (shippingQuote?.mode === 'single_package' && !selectedShippingOption) {
      setError('Please select a shipping method.');
      return;
    }
    if (shippingQuote?.mode === 'split_packages') {
      const missing = splitPackages.some(
        (pkg) => !pkg.options.find((o) => selectedPackageKeys[pkg.packageId] === o.key),
      );
      if (missing) {
        setError('Please select a shipping method for each package.');
        return;
      }
    }
    if (!shippingQuote?.mode || shippingQuote.mode === 'none') {
      setError('No live shipping options are available for this address yet.');
      return;
    }
    setFlowStep('payment');
  };

  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setError('');
    dispatch(clearOrderError());
    dispatch(clearPaymentError());
    if (!selectedMethod) {
      setError(t('modamai.checkout.errorNoPaymentMethod'));
      return;
    }
    if (!authToken) {
      setError(t('modamai.checkout.errorSignInRequired'));
      setFlowStep('account');
      return;
    }
    setSubmitting(true);
    try {
      let discountAmt = 0;
      let couponCodeVal = null;
      if (appliedCoupon?.code?.trim()) {
        const v = await dispatch(
          validateCoupon({ code: appliedCoupon.code.trim(), subtotal, items }),
        );
        if (!v?.success || !v.data || !(Number(v.data.discountAmount) > 0)) {
          setAppliedCoupon(null);
          setError(v?.message || v?.error || t('modamai.checkout.errorCouponRevalidate'));
          setSubmitting(false);
          return;
        }
        discountAmt = Number(v.data.discountAmount);
        couponCodeVal = v.data.code;
        setAppliedCoupon({ code: couponCodeVal, discountAmount: discountAmt });
      }

      const afterDisc = Math.max(0, subtotal - discountAmt);
      const orderTax = Math.round((afterDisc * taxPct) / 100);
      const orderTotal = afterDisc + shippingCost + orderTax;

      const orderData = {
        items: items.map((item) => ({
          productId: item.product?.mongoId || item.product?.id,
          variantId: item.product?.variantId || '',
          product: item.product,
          name: item.product?.name,
          price: item.product?.price,
          quantity: item.qty,
          selectedColor: item.color,
          selectedSize: item.size,
        })),
        shipping: {
          ...shipping,
          address: shipping.address,
          street: shipping.address,
          apartment: shipping.apartment,
          cost: shippingCost,
        },
        shippingAddress: {
          firstName: shipping.firstName,
          lastName: shipping.lastName,
          email: shipping.email,
          phone: shipping.phone,
          address: shipping.address,
          street: shipping.address,
          address2: shipping.apartment,
          city: shipping.city,
          state: shipping.state,
          zipCode: shipping.zipCode,
          country: shipping.country,
        },
        payment: {
          method: selectedMethod?.type || 'flutterwave',
          amount: orderTotal,
        },
        shippingOption:
          shippingQuote?.mode === 'single_package' && selectedShippingOption
            ? {
                mode: 'cj_dynamic',
                methodName: selectedShippingOption.methodName || '',
                etaMinDays: selectedShippingOption.etaMinDays ?? null,
                etaMaxDays: selectedShippingOption.etaMaxDays ?? null,
                customerShippingCost: Number(selectedShippingOption.customerShippingCost || 0),
                destinationCountryCode: normalizeCountryCode(shipping.country),
              }
            : shippingQuote?.mode === 'split_packages'
              ? {
                  mode: 'cj_dynamic_split',
                  destinationCountryCode: normalizeCountryCode(shipping.country),
                  packages: splitPackages.map((pkg) => {
                    const chosen = pkg.options.find((o) => o.key === selectedPackageKeys[pkg.packageId]) || null;
                    return {
                      packageId: pkg.packageId,
                      productId: pkg?.itemRef?.productId || null,
                      productName: pkg?.itemRef?.productName || null,
                      methodName: chosen?.methodName || null,
                      etaMinDays: chosen?.etaMinDays ?? null,
                      etaMaxDays: chosen?.etaMaxDays ?? null,
                      customerShippingCost: Number(chosen?.customerShippingCost || 0),
                    };
                  }),
                }
              : undefined,
        subtotal,
        shippingCost: shippingCost,
        tax: orderTax,
        total: orderTotal,
        ...(couponCodeVal && discountAmt > 0 ? { couponCode: couponCodeVal, discountAmount: discountAmt } : {}),
        ...(cartSessionId ? { cartSessionId } : {}),
      };

      const orderResult = await dispatch(createOrder(orderData));
      if (!orderResult.success) {
        setError(orderResult.error || t('modamai.checkout.errorOrderCreate'));
        setSubmitting(false);
        return;
      }
      const created = unwrapCreatedOrder(orderResult.order);
      const orderId = created?._id || created?.id;
      if (!orderId) {
        setError(t('modamai.checkout.errorOrderCreate'));
        setSubmitting(false);
        return;
      }
      setOrderNumber(created.orderNumber || '');

      if (selectedMethod?.type === 'bank_transfer') {
        const cur = created.currency || 'USD';
        const bt = await paymentService.getBankTransferDetails(orderId, cur);
        const bd = bt?.data ?? bt;
        if (bd && (bd.bankDetails || bd.reference)) {
          setBankDetails(bd);
          clear();
          setFlowStep('bank_transfer');
        } else {
          setError(bt?.message || t('modamai.checkout.errorBankDetails'));
        }
      } else if (selectedMethod?.type === 'payment_link') {
        const url = selectedMethod?.config?.paymentLinkUrl;
        const label = selectedMethod?.config?.paymentLinkLabel || selectedMethod?.name || t('modamai.checkout.payNow');
        setPaymentLink({
          orderNumber: created.orderNumber,
          orderId,
          amount: orderTotal,
          currency: created.currency || 'USD',
          paymentLinkUrl: url,
          paymentLinkLabel: label,
        });
        clear();
        setFlowStep('payment_link');
      } else if (selectedMethod?.type === 'payment_link_email') {
        setEmailPayInfo({
          orderNumber: created.orderNumber,
          orderId,
          amount: orderTotal,
          currency: created.currency || 'USD',
          title: selectedMethod?.config?.title || '',
          description: selectedMethod?.config?.description || '',
          emailWithinHours: selectedMethod?.config?.emailDeliveryWithinHours ?? 24,
          paymentLinkLabel: selectedMethod?.config?.paymentLinkLabel || '',
        });
      clear();
        setFlowStep('payment_link_email');
      } else {
        const customer = {
          email: shipping.email,
          phone: shipping.phone,
          name: `${shipping.firstName} ${shipping.lastName}`,
        };
        const gateway = selectedMethod?.type === 'squad' ? 'squad' : 'flutterwave';
        sessionStorage.setItem('paymentGateway', gateway);
        const payRes = await dispatch(initializePayment(orderId, customer, gateway));
        if (!payRes.success) {
          setError(payRes.error || t('modamai.checkout.errorStartPayment'));
          setSubmitting(false);
          return;
        }
        const url = payRes.data?.paymentUrl;
        if (url) {
          window.location.href = url;
        } else {
          setError(t('modamai.checkout.errorNoPaymentUrl'));
        }
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || t('modamai.checkout.errorCheckoutFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (!isApiEnabled()) {
    return (
      <Container style={{ padding: '4rem 1.5rem', maxWidth: 560 }}>
        <PageSeo documentTitle={t('modamai.checkout.pageTitle')} description={t('modamai.checkout.descriptionApiOff')} canonicalPath="/checkout" />
        <h1 className="font-display" style={{ fontSize: '2rem' }}>{t('modamai.checkout.apiDisabledTitle')}</h1>
        <p style={{ color: '#524D46', lineHeight: 1.6 }}>{t('modamai.checkout.apiDisabledBody')}</p>
        <Button as={Link} to="/" $variant="luxe" style={{ marginTop: '1rem' }}>{t('modamai.checkout.home')}</Button>
      </Container>
    );
  }

  if (
    items.length === 0 &&
    flowStep !== 'confirmation' &&
    flowStep !== 'bank_transfer' &&
    flowStep !== 'payment_link' &&
    flowStep !== 'payment_link_email'
  ) {
    return (
      <Container style={{ padding: '8rem 1.5rem', textAlign: 'center' }}>
        <PageSeo documentTitle={t('modamai.checkout.pageTitle')} description={t('modamai.checkout.descriptionEmptyBag')} canonicalPath="/checkout" />
        <h1 className="font-display" style={{ fontSize: '3rem', margin: '0 0 1rem' }}>{t('modamai.checkout.emptyBagTitle')}</h1>
        <Button as={Link} to="/shop/new" $variant="luxe">{t('modamai.checkout.emptyBagCta')}</Button>
      </Container>
    );
  }

  return (
    <Container
      style={{
        padding: 'clamp(2.5rem,5vw,4rem) 1.5rem',
        display: 'grid',
        gap: 'clamp(2rem,4vw,4rem)',
      }}
      className="chk-grid"
    >
      <PageSeo documentTitle={t('modamai.checkout.pageTitle')} description={t('modamai.checkout.descriptionDefault')} canonicalPath="/checkout" />
      <style>{`@media(min-width:1024px){.chk-grid{grid-template-columns:1fr 400px!important}}`}</style>
      <div>
        {breadcrumbLabel ? (
          <div className="font-mono-luxe" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '2.5rem', fontSize: '11px', color: '#524D46' }}>
            <span style={{ color: '#000' }}>{breadcrumbLabel}</span>
            <ChevronRight size={12} style={{ opacity: 0.35 }} aria-hidden />
            <span style={{ opacity: 0.55 }}>{t('modamai.checkout.secureCheckout')}</span>
          </div>
        ) : null}

        {error ? (
          <p role="alert" style={{ margin: '0 0 1rem', padding: '12px 14px', background: 'hsla(0,60%,96%,1)', color: 'hsl(0,50%,30%)', fontSize: '0.875rem' }}>
            {error}
          </p>
        ) : null}

        {flowStep === 'confirmation' && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.thankYou')}</h1>
            <p style={{ color: '#524D46', marginBottom: '1.5rem' }}>
              {orderNumber ? t('modamai.checkout.orderNumberIs', { orderNumber }) : t('modamai.checkout.orderPlacedShort')}{' '}
              {t('modamai.checkout.confirmationEmail')}
            </p>
            <Button as={Link} to="/" $variant="luxe">{t('modamai.checkout.continueShopping')}</Button>
            </div>
        )}

        {flowStep === 'bank_transfer' && bankDetails && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.bankTransfer')}</h1>
            <p style={{ color: '#524D46', marginBottom: '1rem' }}>{t('modamai.checkout.bankTransferLead')}</p>
            <pre style={{ whiteSpace: 'pre-wrap', fontSize: 13, padding: '1rem', border: '1px solid hsl(35,18%,84%)', background: '#faf8f5' }}>
              {JSON.stringify(bankDetails, null, 2)}
            </pre>
            <Button as={Link} to="/" $variant="luxe" style={{ marginTop: '1rem' }}>{t('modamai.checkout.home')}</Button>
        </div>
        )}

        {flowStep === 'payment_link' && paymentLink && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.payOnline')}</h1>
            <p style={{ marginBottom: '1rem' }}>
              {t('modamai.checkout.orderSummaryLine', {
                orderNumber: paymentLink.orderNumber,
                amount: formatPrice(paymentLink.amount),
              })}
            </p>
            {paymentLink.paymentLinkUrl ? (
              <Button as="a" href={paymentLink.paymentLinkUrl} $variant="luxe" target="_blank" rel="noreferrer">
                {paymentLink.paymentLinkLabel} <ExternalLink size={14} style={{ marginLeft: 6 }} />
              </Button>
            ) : (
              <p>{t('modamai.checkout.payLinkMissing')}</p>
            )}
          </div>
        )}

        {flowStep === 'payment_link_email' && emailPayInfo && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.checkEmail')}</h1>
            <p style={{ color: '#524D46', lineHeight: 1.6 }}>
              {t('modamai.checkout.paymentLinkEmailBody', {
                orderNumber: emailPayInfo.orderNumber,
                hours: emailPayInfo.emailWithinHours,
              })}
            </p>
            {emailPayInfo.title ? <p style={{ fontWeight: 600 }}>{emailPayInfo.title}</p> : null}
            {emailPayInfo.description ? <p style={{ fontSize: '0.875rem' }}>{emailPayInfo.description}</p> : null}
            <Button as={Link} to="/" $variant="luxe" style={{ marginTop: '1rem' }}>{t('modamai.checkout.home')}</Button>
          </div>
        )}

        {flowStep === 'account' && (
          <div>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1.5rem' }}>{t('modamai.checkout.accountHeading')}</h1>
            <p style={{ fontSize: '0.875rem', color: '#524D46', marginBottom: '1.5rem' }}>
              {t('modamai.checkout.accountIntro')}{' '}
              <Link to="/login" style={{ color: '#000', textDecoration: 'underline' }}>{t('modamai.checkout.signInInline')}</Link>.
            </p>
            <form onSubmit={handleAccountRegister} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
              <Field label={t('modamai.checkout.fieldEmail')} type="email" required value={account.email} onChange={(e) => setAccount((a) => ({ ...a, email: e.target.value }))} />
              <div style={{ display: 'grid', gap: '1rem' }} className="co-two">
                <style>{`@media(min-width:640px){.co-two{grid-template-columns:1fr 1fr!important}}`}</style>
                <Field label={t('modamai.checkout.fieldFirstName')} required value={account.firstName} onChange={(e) => setAccount((a) => ({ ...a, firstName: e.target.value }))} />
                <Field label={t('modamai.checkout.fieldLastName')} required value={account.lastName} onChange={(e) => setAccount((a) => ({ ...a, lastName: e.target.value }))} />
              </div>
              <Field label={t('modamai.checkout.fieldPhone')} type="tel" value={account.phone} onChange={(e) => setAccount((a) => ({ ...a, phone: e.target.value }))} />
              <Field label={t('modamai.checkout.fieldPassword')} type="password" required value={account.password} onChange={(e) => setAccount((a) => ({ ...a, password: e.target.value }))} />
              <Field label={t('modamai.checkout.fieldConfirmPassword')} type="password" required value={account.confirm} onChange={(e) => setAccount((a) => ({ ...a, confirm: e.target.value }))} />
              <Button type="submit" $variant="luxe" $size="lg" disabled={submitting}>
                {submitting ? t('modamai.checkout.pleaseWait') : t('modamai.checkout.createAccountContinue')}
              </Button>
            </form>
            <hr style={{ margin: '2rem 0', border: 'none', borderTop: '1px solid hsl(35,18%,84%)' }} />
            <h2 className="font-mono-luxe" style={{ fontSize: '11px', letterSpacing: '0.2em', marginBottom: '1rem' }}>{t('modamai.checkout.alreadyRegistered')}</h2>
            <form onSubmit={handleLogin} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <Field label={t('modamai.checkout.fieldEmail')} type="email" value={loginEmail} onChange={(e) => setLoginEmail(e.target.value)} required />
              <Field label={t('modamai.checkout.fieldPassword')} type="password" value={loginPassword} onChange={(e) => setLoginPassword(e.target.value)} required />
              <Button type="submit" $variant="outlineLuxe" $size="lg" disabled={submitting}>
                {t('modamai.checkout.signInButton')}
              </Button>
            </form>
          </div>
        )}

        {flowStep === 'shipping' && (
          <form onSubmit={advanceFromShipping} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.shippingHeading')}</h1>
            <div style={{ display: 'grid', gap: '1rem' }} className="co-two">
              <style>{`@media(min-width:640px){.co-two{grid-template-columns:1fr 1fr!important}}`}</style>
              <Field label={t('modamai.checkout.fieldFirstName')} required value={shipping.firstName} onChange={setShip('firstName')} />
              <Field label={t('modamai.checkout.fieldLastName')} required value={shipping.lastName} onChange={setShip('lastName')} />
            </div>
            <Field label={t('modamai.checkout.fieldEmail')} type="email" required value={shipping.email} onChange={setShip('email')} />
            <Field label={t('modamai.checkout.fieldPhone')} type="tel" value={shipping.phone} onChange={setShip('phone')} />
            <Field label={t('modamai.checkout.fieldAddress')} required value={shipping.address} onChange={setShip('address')} />
            <Field label={t('modamai.checkout.fieldApartment')} value={shipping.apartment} onChange={setShip('apartment')} />
              <div style={{ display: 'grid', gap: '1rem' }} className="co-three">
                <style>{`@media(min-width:640px){.co-three{grid-template-columns:repeat(3,1fr)!important}}`}</style>
              <Field label={t('modamai.checkout.fieldCity')} required value={shipping.city} onChange={setShip('city')} />
              <Field label={t('modamai.checkout.fieldState')} required value={shipping.state} onChange={setShip('state')} />
              <Field label={t('modamai.checkout.fieldZip')} required value={shipping.zipCode} onChange={setShip('zipCode')} />
              </div>
            <SelectField label={t('modamai.checkout.fieldCountry')} required value={shipping.country} onChange={setShip('country')}>
              {shippingCountries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </SelectField>
            <div style={{ border: '1px solid hsl(35,18%,84%)', padding: '1rem' }}>
              <p className="font-mono-luxe" style={{ margin: '0 0 0.75rem', fontSize: '11px' }}>{t('modamai.checkout.shippingMethod')}</p>
              {shippingLoading ? (
                <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.7 }}>Loading live shipping options...</p>
              ) : shippingQuote?.mode === 'single_package' && singleDisplayOptions.length > 0 ? (
                <div style={SHIPPING_LIST_SCROLL_STYLE}>
                  {singleDisplayOptions.map((opt, idx) => (
                    <label
                      key={opt.key}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        padding: '0.75rem',
                        border: '1px solid hsl(35,18%,84%)',
                        borderRadius: 10,
                        marginBottom: '0.5rem',
                        cursor: 'pointer',
                        background: selectedShippingKey === opt.key ? 'hsl(38, 35%, 94%)' : '#fff',
                      }}
                    >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <input type="radio" name="ship" checked={selectedShippingKey === opt.key} onChange={() => setSelectedShippingKey(opt.key)} style={{ accentColor: '#000' }} />
                        {(() => {
                          const Icon = shippingMethodIcon(opt?.methodName);
                          return <Icon size={16} />;
                        })()}
                        <span style={{ fontSize: '0.875rem' }}>
                          {opt.methodName || 'Shipping'}
                          {idx === 0 && shippingQuote?.fastestOption?.methodName && String(opt.methodName || '').toLowerCase() === String(shippingQuote.fastestOption.methodName).toLowerCase() ? ' (Fastest)' : ''}
                        </span>
                      </span>
                      <span style={{ fontSize: '0.875rem', fontWeight: 500 }}>{opt.etaLabel || `${opt.etaMinDays ?? '?'}-${opt.etaMaxDays ?? '?'} days`}</span>
                    </label>
                  ))}
                </div>
              ) : shippingQuote?.mode === 'split_packages' && splitPackages.length > 0 ? (
                <div style={{ display: 'grid', gap: '0.9rem' }}>
                  <p style={{ margin: 0, fontSize: '0.8rem', opacity: 0.7 }}>
                    Items may ship in separate packages. Choose a method for each package.
                  </p>
                  {splitPackages.map((pkg) => (
                    <div key={pkg.packageId} style={{ border: '1px solid hsl(35,18%,84%)', padding: '0.65rem 0.75rem' }}>
                      <p style={{ margin: '0 0 0.45rem', fontSize: '0.78rem', letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.8 }}>
                        {pkg?.itemRef?.productName || pkg.packageId}
                      </p>
                      <div style={{ ...SHIPPING_LIST_SCROLL_STYLE, maxHeight: '220px', padding: '0 0.5rem' }}>
                        {pkg.options.map((opt) => (
                          <label
                            key={opt.key}
                            style={{
                              display: 'flex',
                              justifyContent: 'space-between',
                              padding: '0.5rem',
                              border: '1px solid hsl(35,18%,84%)',
                              borderRadius: 10,
                              marginBottom: '0.45rem',
                              cursor: 'pointer',
                              background:
                                selectedPackageKeys[pkg.packageId] === opt.key ? 'hsl(38, 35%, 94%)' : '#fff',
                            }}
                          >
                            <span style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                              <input
                                type="radio"
                                name={`ship-${pkg.packageId}`}
                                checked={selectedPackageKeys[pkg.packageId] === opt.key}
                                onChange={() =>
                                  setSelectedPackageKeys((prev) => ({ ...prev, [pkg.packageId]: opt.key }))
                                }
                                style={{ accentColor: '#000' }}
                              />
                              {(() => {
                                const Icon = shippingMethodIcon(opt?.methodName);
                                return <Icon size={16} />;
                              })()}
                              <span style={{ fontSize: '0.84rem' }}>
                                {opt.methodName || 'Shipping'}
                              </span>
                            </span>
                            <span style={{ fontSize: '0.84rem', fontWeight: 500 }}>
                              {opt.etaLabel || `${opt.etaMinDays ?? '?'}-${opt.etaMaxDays ?? '?'} days`}
                    </span>
                  </label>
                ))}
              </div>
                    </div>
                  ))}
                </div>
              ) : (
                <p style={{ margin: 0, fontSize: '0.875rem', opacity: 0.75 }}>
                  No live shipping methods for this address yet. Update destination details and try again.
                </p>
              )}
              </div>
            <Button type="submit" $variant="luxe" $size="lg">
              {t('modamai.checkout.continueToPayment')}
            </Button>
          </form>
        )}

        {flowStep === 'payment' && (
          <form onSubmit={handlePlaceOrder} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <h1 className="font-display" style={{ fontSize: 'clamp(2rem,4vw,3rem)', margin: '0 0 1rem' }}>{t('modamai.checkout.paymentHeading')}</h1>
            <p style={{ fontSize: '0.875rem', color: '#524D46', margin: 0 }}>
              {t('modamai.checkout.totalLine', {
                total: formatPrice(total),
                tax: formatPrice(tax),
                shipping: shippingCost === 0 ? t('modamai.checkout.free') : formatPrice(shippingCost),
              })}
            </p>
            {paymentMethodsLoading ? (
              <p style={{ fontSize: '0.875rem', opacity: 0.65 }}>{t('modamai.checkout.loadingMethods')}</p>
            ) : activeMethods.length === 0 ? (
              <p style={{ fontSize: '0.875rem' }}>{t('modamai.checkout.noActiveMethods')}</p>
            ) : (
              activeMethods.map((m) => (
                <label
                  key={String(m._id || m.type)}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    padding: '14px 16px',
                    border: `1px solid ${selectedMethod?._id === m._id ? '#000' : 'hsl(35,18%,84%)'}`,
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="radio"
                    name="paym"
                    checked={selectedMethod?._id === m._id}
                    onChange={() => setSelectedMethod(m)}
                    style={{ accentColor: '#000' }}
                  />
                  <span style={{ fontSize: '0.875rem' }}>{m.name || m.type}</span>
                </label>
              ))
            )}
              <p style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.75rem', opacity: 0.65, margin: 0 }}>
              <Lock size={14} /> {t('modamai.checkout.cardNote')}
            </p>
            <div style={{ display: 'flex', gap: '0.75rem' }}>
              <Button type="button" $variant="outlineLuxe" $size="lg" onClick={() => setFlowStep('shipping')}>
                {t('modamai.checkout.back')}
              </Button>
              <Button type="submit" $variant="luxe" $size="lg" style={{ flex: 1 }} disabled={submitting || !selectedMethod}>
                {submitting ? t('modamai.checkout.processing') : t('modamai.checkout.placeOrder', { amount: formatPrice(total) })}
            </Button>
          </div>
        </form>
        )}
      </div>

      {(flowStep === 'shipping' || flowStep === 'payment' || flowStep === 'account') && items.length > 0 ? (
      <aside style={{ background: 'hsla(38,25%,90%,0.5)', padding: 'clamp(1.5rem,3vw,2rem)', alignSelf: 'start' }}>
          <h2 className="font-mono-luxe" style={{ margin: '0 0 1.25rem', fontSize: '11px', letterSpacing: '0.25em' }}>{t('modamai.checkout.orderSummary')}</h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', maxHeight: '20rem', overflowY: 'auto' }}>
          {items.map((it, i) => (
            <div key={i} style={{ display: 'flex', gap: '0.75rem' }}>
              <div style={{ position: 'relative', flexShrink: 0 }}>
                <img src={it.product.image} alt={it.product.name} style={{ width: '4rem', height: '5rem', objectFit: 'cover', background: 'hsl(38,25%,90%)' }} />
                <span style={{ position: 'absolute', top: '-0.5rem', right: '-0.5rem', width: '1.25rem', height: '1.25rem', borderRadius: '50%', background: '#000', color: '#f5f2eb', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{it.qty}</span>
              </div>
              <div style={{ flex: 1, fontSize: '0.875rem' }}>
                <p style={{ margin: 0, fontWeight: 500, lineHeight: 1.25 }}>{it.product.name}</p>
                <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', opacity: 0.55 }}>{it.color} · {it.size}</p>
              </div>
              <p style={{ margin: 0, fontSize: '0.875rem' }}>{formatPrice(it.product.price * it.qty)}</p>
            </div>
          ))}
        </div>
          <div style={{ paddingTop: '1rem', marginTop: '1rem', borderTop: '1px solid hsl(35,18%,84%)' }}>
            <p className="font-mono-luxe" style={{ margin: '0 0 0.5rem', fontSize: '10px', letterSpacing: '0.15em', opacity: 0.55 }}>
              {t('modamai.checkout.promoCode')}
            </p>
            {appliedCoupon ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '0.8125rem' }}>
                  <strong>{appliedCoupon.code}</strong> {t('modamai.checkout.applied')}
                </span>
                <Button type="button" $variant="outlineLuxe" $size="sm" onClick={handleRemoveCoupon}>
                  {t('modamai.checkout.remove')}
                </Button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                <Input
                  value={couponInput}
                  onChange={(e) => {
                    setCouponInput(e.target.value);
                    setCouponInlineError('');
                  }}
                  placeholder={t('modamai.checkout.codePlaceholder')}
                  disabled={couponValidating}
                  style={{ flex: 1, fontSize: '0.8125rem' }}
                />
                <Button type="button" $variant="outlineLuxe" $size="sm" onClick={handleApplyCoupon} disabled={couponValidating}>
                  {couponValidating ? '…' : t('modamai.checkout.apply')}
                </Button>
              </div>
            )}
            {couponInlineError ? (
              <p style={{ margin: '0 0 0.75rem', fontSize: '0.75rem', color: 'hsl(0,50%,36%)' }}>{couponInlineError}</p>
            ) : null}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', fontSize: '0.875rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: 0.55 }}>{t('modamai.checkout.subtotal')}</span><span>{formatPrice(subtotal)}</span></div>
            {discountAmount > 0 ? (
              <div style={{ display: 'flex', justifyContent: 'space-between', color: 'hsl(120,25%,28%)' }}>
                <span style={{ opacity: 0.75 }}>{t('modamai.checkout.discount')}</span>
                <span>−{formatPrice(discountAmount)}</span>
        </div>
            ) : null}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: 0.55 }}>{t('modamai.checkout.shipping')}</span><span>{shippingCost === 0 ? t('modamai.checkout.free') : formatPrice(shippingCost)}</span></div>
            <div style={{ display: 'flex', justifyContent: 'space-between' }}><span style={{ opacity: 0.55 }}>{t('modamai.checkout.tax')}</span><span>{formatPrice(tax)}</span></div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '1rem', marginTop: '1rem', borderTop: '1px solid hsl(35,18%,84%)' }}>
            <span className="font-mono-luxe" style={{ fontSize: '11px' }}>{t('modamai.checkout.total')}</span>
          <span style={{ fontSize: '1.25rem' }}>{formatPrice(total)}</span>
        </div>
      </aside>
      ) : null}
    </Container>
  );
}
